import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, CheckCircle, XCircle, Clock, MinusCircle, ChevronRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  delivered:    { label: "Delivered",    color: "#10B981", bg: "#ECFDF5", darkBg: "#052E16", Icon: CheckCircle },
  in_progress:  { label: "In Progress",  color: "#3B82F6", bg: "#EFF6FF", darkBg: "#0A1628", Icon: Clock },
  broken:       { label: "Broken",       color: "#EF4444", bg: "#FEF2F2", darkBg: "#1F0A0A", Icon: XCircle },
  not_started:  { label: "Not Started",  color: "#6B7280", bg: "#F9FAFB", darkBg: "#111827", Icon: MinusCircle },
};

const CATEGORY_COLORS = {
  Climate: "#10B981", Housing: "#3B82F6", Economy: "#F59E0B", Indigenous: "#DC2626",
  Disability: "#8B5CF6", Environment: "#059669", Integrity: "#6366F1", Defence: "#1E40AF",
  Education: "#0EA5E9", "Social Policy": "#EC4899", "Gambling Reform": "#D97706",
  "Foreign Policy": "#64748B", Energy: "#EF4444", Immigration: "#7C3AED",
  Agriculture: "#065F46", Healthcare: "#0891B2", "Human Rights": "#BE185D", Media: "#7C3AED",
  default: "#6B7280",
};

const STATUS_FILTERS = [
  { id: "all", label: "All Promises" },
  { id: "delivered", label: "Delivered" },
  { id: "in_progress", label: "In Progress" },
  { id: "broken", label: "Broken" },
  { id: "not_started", label: "Not Started" },
];

export default function PromisesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [promises, setPromises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.append("status", statusFilter);
    if (categoryFilter !== "all") params.append("category", categoryFilter);
    if (search) params.append("search", search);
    axios.get(`${API}/promises?${params}`)
      .then((r) => setPromises(r.data))
      .catch(() => setPromises([]))
      .finally(() => setLoading(false));
  }, [statusFilter, categoryFilter, search]);

  // Summary stats (from all promises, not filtered)
  const [allPromises, setAllPromises] = useState([]);
  useEffect(() => {
    axios.get(`${API}/promises`).then((r) => setAllPromises(r.data)).catch(() => {});
  }, []);

  const total = allPromises.length;
  const delivered = allPromises.filter((p) => p.status === "delivered").length;
  const inProgress = allPromises.filter((p) => p.status === "in_progress").length;
  const broken = allPromises.filter((p) => p.status === "broken").length;
  const notStarted = allPromises.filter((p) => p.status === "not_started").length;
  const deliveryRate = total > 0 ? Math.round((delivered / total) * 100) : 0;

  const allCategories = [...new Set(allPromises.map((p) => p.category))].sort();

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="promises-page">
      {/* Hero */}
      <div className="py-10 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate("/politicians")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
            data-testid="back-politicians-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All Politicians
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="px-3 py-1 text-xs font-black border-2 border-black" style={{ backgroundColor: "#FFC82E", color: "#000" }}>
              ACCOUNTABILITY
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Campaign Promises Tracker
          </h1>
          <p className="text-sm max-w-xl" style={{ color: "#94A3B8" }}>
            Holding politicians accountable — tracking every promise made and whether it was delivered, broken, or ignored.
          </p>

          {/* Summary stats */}
          {total > 0 && (
            <div className="flex flex-wrap gap-3 mt-6">
              {[
                { label: "Total Promises", value: total, color: "#FFC82E" },
                { label: "Delivered", value: delivered, color: "#10B981" },
                { label: "In Progress", value: inProgress, color: "#3B82F6" },
                { label: "Broken", value: broken, color: "#EF4444" },
                { label: "Not Started", value: notStarted, color: "#6B7280" },
                { label: "Delivery Rate", value: `${deliveryRate}%`, color: "#FFC82E" },
              ].map(({ label, value, color }) => (
                <div key={label} className="border-2 border-black px-4 py-2" style={{ backgroundColor: color + "15" }} data-testid={`promise-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
                  <span className="text-xl font-black" style={{ color }}>{value}</span>
                  <span className="text-xs font-bold ml-2 text-white">{label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Delivery bar */}
          {total > 0 && (
            <div className="mt-4 h-3 max-w-lg border-2 border-white overflow-hidden flex">
              <div className="h-full" style={{ width: `${deliveryRate}%`, backgroundColor: "#10B981" }} title={`${deliveryRate}% delivered`} />
              <div className="h-full" style={{ width: `${Math.round(inProgress / total * 100)}%`, backgroundColor: "#3B82F6" }} title="In progress" />
              <div className="h-full" style={{ width: `${Math.round(broken / total * 100)}%`, backgroundColor: "#EF4444" }} title="Broken" />
              <div className="h-full flex-1" style={{ backgroundColor: "#6B7280" }} title="Not started" />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-col gap-3 mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textSec }} />
            <input
              type="text"
              placeholder="Search by candidate, party, promise or evidence…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border-2 border-black text-sm font-medium outline-none"
              style={{ backgroundColor: surface, color: text }}
              data-testid="promise-search-input"
            />
          </div>

          {/* Status filters */}
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map(({ id, label }) => {
              const cfg = STATUS_CONFIG[id];
              const active = statusFilter === id;
              return (
                <button
                  key={id}
                  onClick={() => setStatusFilter(id)}
                  data-testid={`status-filter-${id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-black border-2 border-black transition-all"
                  style={{
                    backgroundColor: active ? (cfg?.color || "#FFC82E") : surface,
                    color: active ? (id === "all" ? "#000" : "#fff") : text,
                    boxShadow: active ? "2px 2px 0 #000" : "none",
                  }}
                >
                  {cfg?.Icon && <cfg.Icon className="w-3 h-3" />}
                  {label}
                </button>
              );
            })}
          </div>

          {/* Category filters */}
          {allCategories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setCategoryFilter("all")}
                data-testid="category-filter-all"
                className="px-3 py-1 text-xs font-bold border-2 border-black"
                style={{ backgroundColor: categoryFilter === "all" ? "#FFC82E" : surface, color: categoryFilter === "all" ? "#000" : text }}
              >
                All Topics
              </button>
              {allCategories.map((cat) => {
                const color = CATEGORY_COLORS[cat] || CATEGORY_COLORS.default;
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    data-testid={`category-filter-${cat}`}
                    className="px-3 py-1 text-xs font-bold border-2 border-black"
                    style={{
                      backgroundColor: categoryFilter === cat ? color : surface,
                      color: categoryFilter === cat ? "#fff" : text,
                      boxShadow: categoryFilter === cat ? "2px 2px 0 #000" : "none",
                    }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Results count */}
        <p className="text-sm font-bold mb-4" style={{ color: textSec }} data-testid="promise-count">
          {loading ? "Loading…" : `${promises.length} promise${promises.length !== 1 ? "s" : ""}`}
        </p>

        {/* Promise cards */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="border-2 border-black h-32 animate-pulse" style={{ backgroundColor: surface }} />)}
          </div>
        ) : promises.length === 0 ? (
          <div className="border-2 border-black p-10 text-center" style={{ backgroundColor: surface }} data-testid="no-promises">
            <p className="font-black text-base" style={{ color: text }}>No promises found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="promises-grid">
            {promises.map((promise) => {
              const statusCfg = STATUS_CONFIG[promise.status] || STATUS_CONFIG.not_started;
              const catColor = CATEGORY_COLORS[promise.category] || CATEGORY_COLORS.default;
              const cardBg = isDark ? statusCfg.darkBg : statusCfg.bg;
              const StatusIcon = statusCfg.Icon;
              return (
                <div
                  key={promise.id}
                  className="border-2 border-black overflow-hidden"
                  style={{ backgroundColor: cardBg, boxShadow: "3px 3px 0 #000" }}
                  data-testid={`promise-card-${promise.id}`}
                >
                  <div className="h-1" style={{ backgroundColor: statusCfg.color }} />
                  <div className="p-4">
                    {/* Status + category */}
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <div
                        className="flex items-center gap-1 px-2 py-0.5 border-2 border-black text-xs font-black text-white"
                        style={{ backgroundColor: statusCfg.color }}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {statusCfg.label}
                      </div>
                      <span className="text-xs font-bold px-1.5 py-0.5 border" style={{ color: catColor, borderColor: catColor }}>
                        {promise.category}
                      </span>
                    </div>

                    {/* Promise text */}
                    <p className="font-black text-sm leading-snug mb-2" style={{ color: text }}>
                      {promise.promise}
                    </p>

                    {/* Evidence */}
                    <p className="text-xs leading-relaxed mb-3" style={{ color: textSec }}>
                      {promise.evidence}
                    </p>

                    {/* Politician + source */}
                    <div className="flex items-center justify-between gap-2 pt-2 border-t-2" style={{ borderColor: isDark ? "#334155" : "#E5E7EB" }}>
                      <button
                        onClick={() => navigate(`/politicians/${promise.politician_id}`)}
                        className="flex items-center gap-2 hover:opacity-75 transition-opacity group"
                        data-testid={`promise-politician-link-${promise.id}`}
                      >
                        <div className="w-2.5 h-2.5 border border-black" style={{ backgroundColor: promise.party_color }} />
                        <span className="text-xs font-black group-hover:underline" style={{ color: text }}>
                          {promise.politician_name}
                        </span>
                        <span className="text-xs" style={{ color: textSec }}>
                          {promise.party_name}
                        </span>
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: textSec }} />
                      </button>
                      <span className="text-xs" style={{ color: textSec }}>
                        {promise.source}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
